import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Observable, Subscription } from 'rxjs';

import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';

import { CustomerService } from '../../../../../../../../../../services/customer/customer.service';
import { OrganizationConfigService } from '../../../../../../../../../../services/organization-config/organization-config.service';
import { CustomerMembership } from '../../../../../../../../../../models/membership';
import { MembershipDiscountType } from '../../../../../../../../../../models/membership-enums';

@Component({
  selector: 'app-edit-recurring-customer-membership-recurring-discount',
  templateUrl: './edit-recurring-customer-membership-recurring-discount.component.html',
  styleUrls: ['./edit-recurring-customer-membership-recurring-discount.component.css']
})
export class EditRecurringCustomerMembershipRecurringDiscountComponent implements OnInit, OnDestroy {

  @Input() membership: CustomerMembership;
  @Input() savedCallback: (successMessage: string) => undefined;
  @Input() saveClickedObservable: Observable<void>;
  @Output() loadingEvent = new EventEmitter<boolean>();
  @Output() serverErrorEvent = new EventEmitter<number>();

  public hasExistingDiscount = false;

  public discountType: MembershipDiscountType = MembershipDiscountType.currency;
  public discountAmount: number = null;
  public discountAppliesToAddOns = false;
  public discountName: string = null;
  public discountAssociationId: number = null;
  public discountMembershipId: number = null;
  public removeDiscount = false;

  public commonAssociations: {id: number, name: string}[] = null;
  public commonMemberships: {id: number, name: string}[] = null;

  public loadingSubmit = false;
  public loadingAssociations = true;
  public loadingMemberships = true;

  public errorMessage = '';

  public MembershipDiscountType = MembershipDiscountType;

  private saveClickedSubscription: Subscription;

  constructor(
    public orgConfigService: OrganizationConfigService,
    private activeModal: NgbActiveModal,
    private customerService: CustomerService
  ) {}

  ngOnInit() {
    this.saveClickedSubscription = this.saveClickedObservable.subscribe(() => {
      this.handleSave();
    });

    this.discountType = this.membership.recurring_discount_type;
    this.discountAmount = parseFloat(this.membership.recurring_discount_amount);
    this.discountAppliesToAddOns = this.membership.recurring_discount_applies_to_add_ons;
    this.discountName = this.membership.recurring_discount_name;
    this.discountAssociationId = this.membership.recurring_discount_association_id;
    this.discountMembershipId = this.membership.recurring_discount_membership_id;

    this.hasExistingDiscount = this.discountAmount > 0;

    this.loadAssociations();
    this.loadMemberships();
  }

  ngOnDestroy() {
    this.saveClickedSubscription.unsubscribe();
  }

  private isLoading() {
    return (
      this.loadingSubmit ||
      this.loadingSubmit
    );
  }

  private handleSave() {
    if (this.removeDiscount) {
      this.handleRemoveDiscount();
    } else {
      this.handleEditDiscount();
    }
  }

  private handleEditDiscount() {
    this.loadingSubmit = true;
    this.loadingEvent.emit(this.isLoading());

    this.errorMessage = '';

    let actionName = 'edit';
    if (!this.hasExistingDiscount) {
      actionName = 'add'
    }

    this.customerService
      .editCustomerMembershipRecurringDiscount(
        this.membership.id,
        this.discountType,
        this.discountAmount,
        this.discountAppliesToAddOns,
        this.discountName,
        this.discountAssociationId,
        this.discountMembershipId
      )
      .then(() => {
        this.savedCallback('Discount ' + actionName + 'ed successfully.');
        this.activeModal.close();
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.errorMessage = 'You do not have permission to ' + actionName + ' this discount.';
        } else if (reason.status === 400) {
          this.errorMessage = reason.error;
        } else {
          this.serverErrorEvent.emit(reason.status);
        }
      })
      .finally(() => {
        this.loadingSubmit = false;
        this.loadingEvent.emit(this.isLoading());
      });
  }

  private handleRemoveDiscount() {
    this.loadingSubmit = true;
    this.loadingEvent.emit(this.isLoading());

    this.errorMessage = '';

    this.customerService
      .editCustomerMembershipRecurringDiscount(
        this.membership.id,
        MembershipDiscountType.currency,
        0,
        false,
        '',
        null,
        null
      )
      .then(() => {
        this.savedCallback('Discount removed successfully.');
        this.activeModal.close();
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.errorMessage = 'You do not have permission to remove this discount.';
        } else if (reason.status === 400) {
          this.errorMessage = reason.error;
        } else {
          this.serverErrorEvent.emit(reason.status);
        }
      })
      .finally(() => {
        this.loadingSubmit = false;
        this.loadingEvent.emit(this.isLoading());
      });
  }

  private loadAssociations() {
    this.loadingAssociations = true;
    this.loadingEvent.emit(this.isLoading());

    this.customerService
      .getAssociationMembershipsCommonToCustomers(
        this.membership.all_customers.map(customer => customer.id)
      )
      .then(result => {
        this.commonAssociations = result;
      })
      .catch(reason => {
        this.serverErrorEvent.emit(reason.status);
      })
      .finally(() => {
        this.loadingAssociations = false;
        this.loadingEvent.emit(this.isLoading());
      });
  }

  private loadMemberships() {
    this.loadingMemberships = true;
    this.loadingEvent.emit(this.isLoading());

    this.customerService
      .getMembershipsCommonToCustomers(
        this.membership.all_customers.map(customer => customer.id)
      )
      .then(result => {
        this.commonMemberships = result;
      })
      .catch(reason => {
        this.serverErrorEvent.emit(reason.status);
      })
      .finally(() => {
        this.loadingMemberships = false;
        this.loadingEvent.emit(this.isLoading());
      });
  }

}
